import { zCompanyCreateResultDto, zCompanySetStatusResultDto, zCorpCompanyRowDto, } from "@/types/company/company.dto";
import { zCorpCompanyDom, } from "@/types/company/company.domain";
import { zCorpCompanyStatus } from "@/types/corp/corpSession.common";
const text = (value) => typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
const bankDetailsToDomain = (raw) => {
    if (!raw)
        return null;
    const details = {
        bankName: text(raw.bankName),
        bik: text(raw.bik),
        account: text(raw.account),
        corrAccount: text(raw.corrAccount),
    };
    return Object.values(details).some((value) => value !== null) ? details : null;
};
const bankDetailsToPayload = (details) => {
    if (!details)
        return null;
    const payload = Object.fromEntries(Object.entries(details).filter((entry) => text(entry[1]) !== null));
    return Object.keys(payload).length > 0 ? payload : null;
};
/** Ключи реквизитов — ровно ключи payload `corp_company_create` / `corp_company_update` (§4.3). */
const REQUISITE_KEYS = [
    "name",
    "legalName",
    "inn",
    "kpp",
    "ogrn",
    "legalAddress",
    "bankDetails",
    "contactName",
    "contactPhone",
    "contactEmail",
    "contractNumber",
    "contractDate",
    "comment",
];
export const CompanyMapper = {
    rowToDomain(raw) {
        const row = zCorpCompanyRowDto.parse(raw);
        return zCorpCompanyDom.parse({
            id: row.id,
            name: row.name,
            legalName: row.legal_name ?? null,
            inn: row.inn ?? null,
            kpp: row.kpp ?? null,
            ogrn: row.ogrn ?? null,
            legalAddress: row.legal_address ?? null,
            bankDetails: bankDetailsToDomain(row.bank_details),
            contactName: row.contact_name ?? null,
            contactPhone: row.contact_phone ?? null,
            contactEmail: row.contact_email ?? null,
            contractNumber: row.contract_number ?? null,
            contractDate: row.contract_date ?? null,
            comment: row.comment ?? null,
            status: row.status,
            statusReason: row.status_reason ?? null,
            statusChangedAt: row.status_changed_at ?? null,
            creditState: row.credit_state,
            activeLinksCount: row.active_links_count,
            adminsCount: row.admins_count,
            openOrdersCount: row.open_orders_count,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
        });
    },
    requisitesOf(company) {
        return {
            name: company.name,
            legalName: company.legalName,
            inn: company.inn,
            kpp: company.kpp,
            ogrn: company.ogrn,
            legalAddress: company.legalAddress,
            bankDetails: company.bankDetails,
            contactName: company.contactName,
            contactPhone: company.contactPhone,
            contactEmail: company.contactEmail,
            contractNumber: company.contractNumber,
            contractDate: company.contractDate,
            comment: company.comment,
        };
    },
    /**
     * Payload `corp_company_create`: только заполненные реквизиты. Финансовых ключей нет никогда —
     * при создании действуют значения по умолчанию (P-2), иначе DISPATCHER получил бы 42501.
     */
    toCreatePayload(requisites) {
        const payload = {};
        REQUISITE_KEYS.forEach((key) => {
            const value = key === "bankDetails" ? bankDetailsToPayload(requisites.bankDetails) : text(requisites[key]);
            if (value !== null)
                payload[key] = value;
        });
        return payload;
    },
    /**
     * Patch `corp_company_update` для реквизитов: все ключи реквизитов (пустое значение = очистить поле),
     * без финансовых ключей — поэтому правка реквизитов доступна диспетчеру (D14).
     */
    toRequisitesPatch(requisites) {
        const patch = {};
        REQUISITE_KEYS.forEach((key) => {
            patch[key] =
                key === "bankDetails" ? bankDetailsToPayload(requisites.bankDetails) : text(requisites[key]);
        });
        return patch;
    },
    createResultToDomain(raw) {
        const dto = zCompanyCreateResultDto.parse(raw);
        return {
            companyId: dto.companyId,
            idAdvance: dto.tariff.idAdvance,
            idLateTariff: dto.tariff.idLateTariff ?? null,
        };
    },
    setStatusResultToDomain(raw) {
        const dto = zCompanySetStatusResultDto.parse(raw);
        return {
            companyId: dto.companyId,
            status: zCorpCompanyStatus.parse(dto.status),
            openOrders: dto.openOrders,
            linksEnded: dto.linksEnded ?? null,
        };
    },
};
